import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useVideoConfig, useCurrentFrame } from 'remotion';
import { EffectType } from '../effects';
import { EffectShape, EffectResult, CanvasDimensions } from '../effects/BaseEffect';
import { ObjectFit } from '../types/style';
import { getVideoDimensions, getObjectFitDrawRect } from '../Utils';

interface CanvasRendererForVideoProps {
    blockId: string;
    mediaId: string;
    width: number;
    height: number;
    effects?: EffectType[];
    objectFit?: ObjectFit;
    children: React.ReactElement<{
        onVideoFrame?: (frame: CanvasImageSource) => void;
    }>;
}

// Utility function to apply effects
const applyEffects = (
    ctx: CanvasRenderingContext2D,
    effectsToApply: EffectType[],
    currentFrameNum: number,
    fpsVal: number,
    canvasResWidth: number,
    canvasResHeight: number,
    canvasElWidth: number,
    canvasElHeight: number,
    initialRect: { x: number, y: number, width: number, height: number }
): EffectResult => {
    let effectRes: EffectResult = { shape: 'rect', rect: initialRect };
    effectsToApply.forEach(effect => {
        const currentTime = currentFrameNum / fpsVal;
        const progress = effect.calculateProgress(currentTime);
        effectRes = effect.apply(ctx, {
            resolutionWidth: canvasResWidth,
            resolutionHeight: canvasResHeight,
            elementWidth: canvasElWidth,
            elementHeight: canvasElHeight
        }, progress, effectRes) || effectRes;
    });
    return effectRes;
};

// Utility function to get container styles from effects
const getContainerStyles = (
    effectsForStyle: EffectType[], 
    currentFrameNum: number, 
    fpsVal: number,
    dimensions: CanvasDimensions,
    currentEffectResult: EffectResult
): React.CSSProperties => {
    return effectsForStyle.reduce((styles, effect) => {
        if (effect.containerEffect && effect.getContainerStyle) {
            const currentTime = currentFrameNum / fpsVal;
            const progress = effect.calculateProgress(currentTime);
            return { ...styles, ...effect.getContainerStyle(progress, dimensions, currentEffectResult) };
        }
        return styles;
    }, {});
};

export const CanvasRendererForVideo: React.FC<CanvasRendererForVideoProps> = ({
    blockId,
    mediaId,
    width,
    height,
    effects,
    objectFit = 'contain',
    children
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { fps } = useVideoConfig();
    const currentFrame = useCurrentFrame();

    const [latestFrameImage, setLatestFrameImage] = useState<CanvasImageSource | null>(null);
    const [effectResult, setEffectResult] = useState<EffectResult>({ shape: 'rect', rect: { x: 0, y: 0, width, height } });
    const [containerStyles, setContainerStyles] = useState<React.CSSProperties>({});

    const handleVideoFrameCallback = useCallback((frame: CanvasImageSource) => {
        setLatestFrameImage(frame);
    }, [setLatestFrameImage]); // setLatestFrameImage is stable

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !latestFrameImage) {
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) ctx.clearRect(0, 0, width, height);
            }
            setContainerStyles({});
            setEffectResult({ shape: 'rect', rect: { x: 0, y: 0, width, height } });
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, width, height);

        const { width: videoWidth, height: videoHeight } = getVideoDimensions(latestFrameImage);
        const { drawX, drawY, drawWidth, drawHeight } = getObjectFitDrawRect(
            objectFit,
            videoWidth,
            videoHeight,
            width,
            height
        );

        ctx.save();

        // First apply all effects to set up the canvas context
        let currentEffectResult: EffectResult = { shape: 'rect', rect: { x: drawX, y: drawY, width: drawWidth, height: drawHeight } };
        if (effects && effects.length > 0) {
            currentEffectResult = applyEffects(
                ctx,
                effects.filter(effect => !effect.containerEffect),
                currentFrame,
                fps,
                width,            // canvasResWidth
                height,           // canvasResHeight
                canvas.clientWidth, // canvasElWidth
                canvas.clientHeight,// canvasElHeight
                { x: drawX, y: drawY, width: drawWidth, height: drawHeight }
            );
        }
        setEffectResult(currentEffectResult);

        // Now draw the image after effects have modified the context
        ctx.drawImage(latestFrameImage, drawX, drawY, drawWidth, drawHeight);

        // Handle container styles after drawing
        let currentContainerStyles: React.CSSProperties = {};
        if (effects) {
            currentContainerStyles = getContainerStyles(
                effects, 
                currentFrame, 
                fps,
                {
                    resolutionWidth: width,
                    resolutionHeight: height,
                    elementWidth: canvas.clientWidth,
                    elementHeight: canvas.clientHeight
                },
                currentEffectResult
            );
        }
        setContainerStyles(currentContainerStyles);
        
        ctx.restore();

    }, [
        latestFrameImage, 
        currentFrame, 
        fps, 
        width, 
        height, 
        effects, 
        objectFit,
        // Not including canvasRef.current, setEffectResult, setContainerStyles as they are stable or refs
        // Assuming getVideoDimensions, getObjectFitDrawRect, applyEffects, getContainerStyles are stable utility functions
    ]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative', ...containerStyles }}>
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: objectFit // This CSS objectFit might be redundant given manual drawing control
                }}
            />
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                {React.cloneElement(children, {
                    onVideoFrame: handleVideoFrameCallback
                })}
            </div>
        </div>
    );
}; 